import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./css/homepage.css";
import logo from "./images/logo_ticket_buydeal.jpg";
import heroBg from "./images/hero-bg.jpg";
import { API_URL_BASE } from "./config";
import { FaCalendarAlt, FaWhatsapp, FaEnvelope, FaInstagram,FaFacebookF, FaLinkedinIn} from "react-icons/fa";
import { GoLocation } from "react-icons/go";

const HomePage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [minPrices, setMinPrices] = useState({}); // { eventid: minPriceInCents }
  
  // Fetch all events
  useEffect(() => {
    fetch(API_URL_BASE + '/ticket.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fetchallevents: { token: "PUBLIC" } }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        return res.json();
      })
      .then((result) => {
        const eventsData = result.events.events || [];
        setEvents(eventsData);

        // Fetch ticket prices for all events in parallel
        Promise.all(
          eventsData.map((event) =>
            fetch(API_URL_BASE + '/ticket.json', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ fetchEventDetailForPurchase: { eventId: event.eventid } }),
            })
              .then((r) => r.json())
              .then((d) => {
                const tickets = d?.eventdetails?.tickets || [];
                const prices = tickets.map((t) => t.price).filter((p) => p > 0);
                return { eventid: event.eventid, min: prices.length ? Math.min(...prices) : 0 };
              })
              .catch(() => ({ eventid: event.eventid, min: null }))
          )
        ).then((results) => {
          const priceMap = {};
          results.forEach(({ eventid, min }) => { priceMap[eventid] = min; });
          setMinPrices(priceMap);
        });
      })
      .catch((err) => { console.error("Fetch error:", err); })
      .finally(() => { setLoading(false); });
  }, []);

  return (
    <div className="home">
      {/* NAVBAR */}
      <header className="navbar">
        <div className="navbar-inner">
          <div className="logo" onClick={() => navigate("/")}>
		    <img src={logo} alt=""/>
            <span>Buydeal.EU</span>
          </div>
          <div className="nav-buttons">
		   <div className="nav-menu">
            <a href="/orginfo" className="nav-link"> Event Organisers </a>
			<a href="/homepagehelp" className="nav-link"> Help </a>
			</div>
            <button
              className="btn-primary"
              onClick={() => navigate("/login")}
            >
              Organiser Login
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <div className="hero" 
	  style={{
	    backgroundImage: `url(${heroBg})`,
	    backgroundPosition: "center",
	    backgroundSize: "cover",
	    backgroundRepeat: "no-repeat",
	  }}>
        <div className="hero-content">
          <h1>Discover Events Near You</h1>
          <p>
            Browse upcoming concerts, festivals, meetups and more. Buy tickets
            instantly.
          </p>
          <button
            className="btn-primary hero-btn"
            onClick={() => window.scrollTo({ top: 600, behavior: "smooth" })}
          >
            Explore Events
          </button>
        </div>
      </div>

      {/* EVENTS SECTION */}
      <section className="events-section">
        <h2 className="section-title">Upcoming Events</h2>

        {loading ? (
          <p className="loading">Loading events...</p>
        ) : (
			<>
          <div className="events-grid">
            {events
				?.filter((event) => new Date(event.startdatetime) >= new Date().setHours(0, 0, 0, 0))
				?.sort((a, b) => new Date(b.startdatetime) - new Date(a.startdatetime))
				.map((event) => {
				  const d = event.startdatetime ? new Date(event.startdatetime) : null;
				  return (
              <div
                key={event.eventid}
                className="event-card"
                onClick={() => navigate(`/buy/${event.eventid}`)}
              >
                <div className="event-image-homepage">
                  {event.image ? (
                    <img
                      src={
                        event.image.startsWith("http")
                          ? event.image
                          : `data:image/png;base64,${event.image}`
                      }
                      alt={event.name}
                      loading="lazy"
                    />
                  ) : (
					  <div className="image-placeholder"><FaCalendarAlt /></div>
					)}
					{d && (
					  <div className="event-date-badge">
					    <span className="badge-day">{d.getDate()}</span>
					    {d.toLocaleString(undefined, { month: 'short' }).toUpperCase()}
					  </div>
                  )}
                </div>

                <div className="event-info">
                  <h3>{event.name}</h3>
                  <p className="event-date">
				 <FaCalendarAlt color="#1E3A8A"/>
                          {event.startdatetime
                      ? new Date(event.startdatetime).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : ""}
                  </p>
                  <p className="event-location"><GoLocation color="#1E3A8A" stroke="#1E3A8A" strokeWidth={1} />  {event.address}</p>
                  <p className="event-price">
                    {event.type?.toUpperCase() === 'FREE'
                      ? 'Gratis'
                      : minPrices[event.eventid] > 0
                      ? `Vanaf € ${(minPrices[event.eventid] / 100).toFixed(2)}`
                      : 'Vanaf € ...'}
                  </p>
                  <button
                    className="btn-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/buy/${event.eventid}`);
                    }}
                  >
                    Buy Tickets
                  </button>
                </div>
              </div>
			  		  );
			  		})}

          </div>
		  
		  <div>
		  	<h2 className="section-title">Past Events</h2> 
			<div className="events-grid">
			  {events
				?.filter((event) => new Date(event.startdatetime) < new Date().setHours(0, 0, 0, 0))
				?.sort((a, b) => new Date(b.startdatetime) - new Date(a.startdatetime))
				.map((event) => (
			    <div
			      key={event.eventid}
			      className="event-card"
			      onClick={() => navigate(`/buy/${event.eventid}`)}
			    >
			      <div className="event-image-homepage">
			        {event.image ? (
			          <img
			            src={
			              event.image.startsWith("http")
			                ? event.image
			                : `data:image/png;base64,${event.image}`
			            }
			            alt={event.name}
			            loading="lazy"
			          />
			        ) : (
			          <div className="image-placeholder">🎉</div>
			        )}
			      </div>

			      <div className="event-info">
			        <h3>{event.name}</h3>
			        <p className="event-date">
						<FaCalendarAlt/> {event.startdatetime
			            ? new Date(event.startdatetime).toLocaleDateString(undefined, {
			                month: "short",
			                day: "numeric",
			                year: "numeric",
			              })
			            : ""}
			        </p>
					<p className="event-location"><GoLocation color="#1E3A8A" stroke="#1E3A8A" strokeWidth={1} />  {event.address}</p>
				<p className="event-price">
				  {event.type?.toUpperCase() === 'FREE'
				    ? 'Gratis'
				    : minPrices[event.eventid] > 0
				    ? `Vanaf € ${(minPrices[event.eventid] / 100).toFixed(2)}`
				    : 'Vanaf € ...'}
				</p>

			      </div>
			    </div>
			  ))}
			</div>
		  </div>
		  </>
        )}
      </section>

      {/* FOOTER */}
	  <footer className="footer">
	    <div className="footer-top">
	      <div className="footer-brand">
	        <div className="footer-logo">
	          <img src={logo} />
	          <span>Buydeal.EU</span>
	        </div>
	        <p className="footer-tagline">
	          A simple and low-cost online ticketing platform for event organisers in Netherlands.
	        </p>
	        <div className="footer-social">
	          <a href="https://linkedin.com/company/buydeal-eu" aria-label="LinkedIn" target="_blank" rel="noreferrer"><FaLinkedinIn/></a>
	          <a href="https://facebook.com/buydeal.eu" aria-label="Facebook" target="_blank" rel="noreferrer"><FaFacebookF/></a>
	          <a href="https://instagram.com/tickets.buydeal" aria-label="Instagram" target="_blank" rel="noreferrer"><FaInstagram/></a>
	        </div>
	      </div>

	      <div className="footer-col">
	        <h4>For Organisers</h4>
	        <ul>
	          <li><a href="/orginfo">How it works</a></li>
	          <li><a href="/login">Organiser Login</a></li>
	          <li><a href="/homepagehelp">Help Center</a></li>
	          <li><a href="/homepagehelp#faq">FAQ</a></li>
	        </ul>
	      </div>

	      <div className="footer-col">
		  <h4>Contact</h4>
		  <ul>
		    <li><FaEnvelope/><a href="mailto:contact@buydeal.eu"> contact@buydeal.eu</a></li>
		    <li><FaWhatsapp/><a href="https://wa.me/31644430900/?text=Hi, I would like to connect with you regarding the event: "> +31 644430900</a></li>
		  </ul>
	      </div>

	      <div className="footer-col">
	        <h4>Company Details</h4>
	        <ul>
			  <li>KVK (Reg. No): 80320929</li>
	          <li><a href="https://maps.google.com" target="_blank" rel="noreferrer">Winthontlaan 200, 3526 KV, Utrecht, Netherlands</a></li>
	        </ul>
	      </div>
	    </div>

	    <div className="footer-bottom">
	      <span>© {new Date().getFullYear()} Buydeal.eu B.V. All Rights Reserved.</span>
	      <div className="footer-legal">
	        <a href="/privacy">Privacy Policy</a>
	        <a href="/toc">Terms &amp; Conditions (Buyers)</a>
	        <a href="/toc?tab=organisers">Terms &amp; Conditions (Organisers)</a>
	      </div>
	    </div>
	  </footer>
    </div>
  );
};

export default HomePage;
